/**
 * Visx Pie/Donut implementation. SVG-native using @visx/shape Pie.
 * Hover-highlight grows the active slice's outer radius (mirrors the
 * Recharts renderActiveShape behavior).
 */

import { useState } from 'react';
import { ParentSize } from '@visx/responsive';
import { Group } from '@visx/group';
import { Pie } from '@visx/shape';
import { Text } from '@visx/text';
import { useTooltip, TooltipWithBounds, defaultStyles } from '@visx/tooltip';
import type { PieChartProps, CommonChartProps, CategoricalDatum } from '../types';
import { chartTheme } from '../theme';
import { DrillAwareTooltip } from '@/components/analytics/DrillAwareTooltip';

type Props = PieChartProps & CommonChartProps;

export function VisxPie(props: Props): JSX.Element {
  return (
    <ParentSize>
      {({ width, height }) =>
        width > 0 && height > 0 ? <VisxPieInner {...props} width={width} height={height} /> : null
      }
    </ParentSize>
  );
}

function VisxPieInner({
  type,
  data,
  minLabelPercent = 0.08,
  enableHover = true,
  width,
  height,
}: Props & { width: number; height: number }): JSX.Element {
  const theme = chartTheme('visx').theme;
  const palette = theme.categoricalPalette;
  const [activeIndex, setActiveIndex] = useState<number | undefined>(undefined);
  const { tooltipOpen, tooltipLeft, tooltipTop, tooltipData, showTooltip, hideTooltip } =
    useTooltip<{ datum: CategoricalDatum; index: number }>();

  const radius = Math.min(width, height) / 2;
  const cx = width / 2;
  const cy = height / 2;
  const innerRadius = type === 'donut' ? radius * 0.55 : 0;
  const total = data.reduce((acc, d) => acc + d.value, 0);

  return (
    <div style={{ position: 'relative', width, height }}>
      <svg width={width} height={height}>
        <Group top={cy} left={cx}>
          <Pie
            data={data}
            pieValue={(d) => d.value}
            outerRadius={radius * 0.92}
            innerRadius={innerRadius}
            padAngle={0.005}
          >
            {(pie) =>
              pie.arcs.map((arc, i) => {
                const fill = (arc.data.color as string) ?? palette[i % palette.length];
                const isActive = enableHover && activeIndex === i;
                const arcPath = pie.path({
                  ...arc,
                  // Active arc grows outward by 6px — mirrors Recharts renderActiveShape.
                  // We achieve this by extending outerRadius for this specific arc via a wrapped pie generator.
                });
                const [centroidX, centroidY] = pie.path.centroid(arc);
                const percent = total > 0 ? arc.value / total : 0;
                const showLabel = percent >= minLabelPercent;

                // Hover scales the entire arc — easiest via transform on the group.
                const arcGroup = (
                  <g
                    key={`arc-${i}`}
                    style={{
                      transform: isActive ? 'scale(1.04)' : 'scale(1)',
                      transformOrigin: '0 0',
                      transition: 'transform 120ms ease-out',
                      cursor: enableHover ? 'pointer' : 'default',
                    }}
                    onMouseEnter={(e) => {
                      if (!enableHover) return;
                      setActiveIndex(i);
                      const rect = (e.currentTarget.ownerSVGElement as SVGSVGElement | null)?.getBoundingClientRect();
                      showTooltip({
                        tooltipLeft: e.clientX - (rect?.left ?? 0),
                        tooltipTop: e.clientY - (rect?.top ?? 0),
                        tooltipData: { datum: arc.data, index: i },
                      });
                    }}
                    onMouseLeave={() => {
                      if (!enableHover) return;
                      setActiveIndex(undefined);
                      hideTooltip();
                    }}
                  >
                    <path d={arcPath ?? ''} fill={fill} />
                    {showLabel && (
                      <Text
                        x={centroidX}
                        y={centroidY}
                        textAnchor="middle"
                        verticalAnchor="middle"
                        fill="#ffffff"
                        fontSize={11}
                        fontWeight={600}
                      >
                        {`${(percent * 100).toFixed(0)}%`}
                      </Text>
                    )}
                  </g>
                );
                return arcGroup;
              })
            }
          </Pie>
        </Group>
      </svg>
      {tooltipOpen && tooltipData && (
        <TooltipWithBounds top={tooltipTop} left={tooltipLeft} style={{ ...defaultStyles, background: 'transparent', padding: 0, boxShadow: 'none' }}>
          <DrillAwareTooltip
            rows={[
              {
                color: (tooltipData.datum.color as string) ?? palette[tooltipData.index % palette.length],
                label: tooltipData.datum.label,
                value: tooltipData.datum.value,
              },
            ]}
          />
        </TooltipWithBounds>
      )}
    </div>
  );
}
